import {Component, inject, OnDestroy, OnInit} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {Store} from "@ngrx/store";
import {AssetActions, AssetCommentaryHistoryActions, AssetNameActions} from "../store/asset.actions";
import {
    selectAssetName,
    selectAssetTemplateContext,
    selectAssetUrl,
    selectBaselineAsset,
    selectClosingDate,
    selectCurrentAssetFundValuationDescription,
    selectIsAnyAssetFormInEditMode,
    selectIsCurrentAssetRealized,
    selectRelatedAssetsInFund,
    selectRelatedDeal,
    selectRelatedFund,
    selectReportDate,
    selectSelectedAsset,
    selectSelectedAssetId
} from "../store/asset/asset.selectors";
import {CommentCountsActions} from "../../comment/store/comment.actions";
import {CARD_COMMENT_CONTEXT_FACTORY, CARD_COMMENT_CONTEXT_PREFIX} from "../../shared/comments/card-context-menu/card-comment-context";
import {createAssetCardCommentContextFactory} from "./asset-card-comment-context.factory";
import {combineLatest, distinctUntilChanged, filter, first, map, Subscription} from "rxjs";
import {FormBuilder, Validators} from "@angular/forms";
import {EMPTY_TEXT, TraceableText} from "../../shared/model/traceable";
import {MatDialog} from "@angular/material/dialog";
import {selectSelectedFundReportMasterId} from "../../fund/store/fund-report/fund-report.selectors";
import {selectPreferredAssetValuationForAssetAndFundValuation, selectPreferredValuationType} from "../store/asset-valuation/asset-valuation.selectors";
import {selectSelectedSourceDataset} from "../../import/store/import.selectors";
import {AssetValuationTypeEnum} from "../models/asset-valuation/asset-valuation-type.enum";
import {ConfirmationComponent} from "../../shared/components/confirmation/confirmation.component";
import {DocumentGeneratorComponent} from "../../shared/components/document-generator/document-generator.component";

@Component({
    selector: "valumize-asset-details-page",
    templateUrl: "./asset-details-page.component.html",
    styleUrls: ["./asset-details-page.component.scss"],
    standalone: false,
    providers: [
        // Page-root comment-context prefix: one bulk-counts call per page covers every card under it.
        // Used by anchor-only asset cards; the fund-view cards resolve through the factory below.
        {
            provide: CARD_COMMENT_CONTEXT_PREFIX,
            useFactory: (store: Store) =>
                store.select(selectSelectedAssetId).pipe(map((id) => id != null ? `/assets/asset_id=${id}` : null)),
            deps: [Store],
        },
        // Scope-aware factory: the fund-view cards (forecast / gp / exits) key their thread to the
        // fund + investment (+ valuation) ids identifying their data (spec §3).
        {
            provide: CARD_COMMENT_CONTEXT_FACTORY,
            useFactory: (store: Store) => createAssetCardCommentContextFactory(store),
            deps: [Store],
        },
    ],
})
export class AssetDetailsPageComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dialog = inject(MatDialog);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);

    assetId?: number;
    fundId?: number;
    fundReportId?: number;
    fundValuationId?: number;
    assetValuationId?: number;
    name: TraceableText = EMPTY_TEXT;
    assetNameIsEditable = false;
    searchEnabled = false;
    preferredValuationType: AssetValuationTypeEnum = AssetValuationTypeEnum.DiscountMultipleValuation;
    dealId?: number;

    selectedAsset$ = this.store.select(selectSelectedAsset);
    baselineAssset$ = this.store.select(selectBaselineAsset);
    assetUrl$ = this.store.select(selectAssetUrl);
    selectedPreferredValuation$ = this.store.select(selectPreferredAssetValuationForAssetAndFundValuation);
    relatedFund$ = this.store.select(selectRelatedFund);
    relatedAssetsInFund$ = this.store.select(selectRelatedAssetsInFund);
    relatedDeal$ = this.store.select(selectRelatedDeal);
    fundBreadcrumbData$ = combineLatest([this.relatedFund$, this.relatedDeal$]).pipe(
        map(([fund, deal]) => ({fund, deal}))
    );
    reportDate$ = this.store.select(selectReportDate);
    closingDate$ = this.store.select(selectClosingDate);
    // Fund-valuation description for the favorite snapshot. Guarded so it only resolves when the fund
    // store's selected valuation matches this asset's related valuation — otherwise undefined (no chip),
    // preventing a stale description leaking from a previously-viewed valuation.
    fundValuationDescription$ = this.store.select(selectCurrentAssetFundValuationDescription);
    sourceDataset$ = this.store.select(selectSelectedSourceDataset);
    isEditDisabled$ = this.store.select(selectIsAnyAssetFormInEditMode);
    isRealized$ = this.store.select(selectIsCurrentAssetRealized);
    //TODO: this will lead to a bug if the user navigates directly to the asset page without loading the fund before
    selectedFundReportMergeTargetId$ = this.store.select(selectSelectedFundReportMasterId);

    subscriptions: Subscription[] = [];

    nameForm = this.formBuilder.group({
        assetName: this.formBuilder.control("", {nonNullable: true, validators: Validators.maxLength(100)}),
    });

    ngOnInit() {
        // The commentary history feeds both narrative cards from one request, and the page owns it so
        // neither card has to know the other exists. Keyed on assetId alone, deliberately: the
        // subscription below re-fires whenever any of the five route params changes, and switching
        // fund valuation or report cannot change commentary that is keyed by asset — hanging this off
        // that stream refetched it on every valuation switch.
        this.subscriptions.push(
            this.route.paramMap.pipe(
                map((paramMap) => this.parseIntOrUndefined(paramMap.get("assetId"))),
                filter((assetId): assetId is number => assetId != null),
                distinctUntilChanged()
            ).subscribe((assetId) => {
                this.store.dispatch(AssetCommentaryHistoryActions.load({assetId}));
            }));

        this.subscriptions.push(
            combineLatest([this.route.paramMap, this.route.queryParamMap]).pipe(
                map(([paramMap, queryParamMap]) => ({
                    assetId: this.parseIntOrUndefined(paramMap.get("assetId")),
                    fundId: this.parseIntOrUndefined(queryParamMap.get("fundId")),
                    fundReportId: this.parseIntOrUndefined(queryParamMap.get("fundReportId")),
                    fundValuationId: this.parseIntOrUndefined(queryParamMap.get("fundValuationId")),
                    dealId: this.parseIntOrUndefined(queryParamMap.get("dealId"))
                })),
                distinctUntilChanged((prev, curr) =>
                    prev.assetId === curr.assetId &&
                    prev.fundId === curr.fundId &&
                    prev.fundReportId === curr.fundReportId &&
                    prev.fundValuationId === curr.fundValuationId &&
                    prev.dealId === curr.dealId
                )
            ).subscribe(params => {
                    if (params.assetId == null) {
                        return;
                    }
                    this.assetId = params.assetId;
                    this.fundId = params.fundId;
                    this.fundReportId = params.fundReportId;
                    this.fundValuationId = params.fundValuationId;
                    this.dealId = params.dealId;

                    this.store.dispatch(AssetActions.loadAssetData({
                        assetId: this.assetId,
                        fundId: this.fundId,
                        fundReportId: this.fundReportId,
                        fundValuationId: this.fundValuationId,
                        dealId: this.dealId
                    }));
                }
            )
        );

        this.subscriptions.push(
            this.store.select(selectAssetName).pipe(map((name) => {
                this.name = name.name;
                this.assetNameIsEditable = name.assetNameIsEditable;
                this.nameForm.patchValue({
                    assetName: name.name?.text
                });
                if (name.assetNameIsEditable) {
                    this.nameForm.enable();
                } else {
                    this.nameForm.disable();
                }
            })).subscribe());

        this.subscriptions.push(
            this.store.select(selectPreferredValuationType).pipe(
                filter((v): v is AssetValuationTypeEnum => v != null),
                distinctUntilChanged()
            ).subscribe(v => {
                this.preferredValuationType = v;
            })
        );

        // One bulk-counts call per page (never per card — perf rule 1), reloaded when the entity id OR
        // the selected dataset changes so the has-comment icons match the current dataset.
        this.subscriptions.push(
            combineLatest([
                this.store.select(selectSelectedAssetId).pipe(filter((id): id is number => id != null)),
                this.store.select(selectSelectedSourceDataset)
            ]).pipe(
                distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1])
            ).subscribe(([id]) =>
                this.store.dispatch(CommentCountsActions.load({inContext: `/assets/asset_id=${id}`}))
            )
        );
    }

    private parseIntOrUndefined(value: string | null): number | undefined {
        if (!value) {
            return undefined;
        }
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? undefined : parsed;
    }

    scrollToComponent(componentId: string) {
        document.getElementById(componentId)?.scrollIntoView({behavior: "auto"});
    }

    navigateTo(target: any) {
        const select = target as HTMLSelectElement;
        const assetId = Number(select.value);
        if (assetId) {
            this.router.navigate(["../../assets/" + assetId], {
                relativeTo: this.route,
                queryParams: {
                    fundId: this.fundId,
                    fundReportId: this.fundReportId,
                    fundValuationId: this.fundValuationId,
                    dealId: this.dealId
                }
            });
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    editMode = () => this.store.dispatch(AssetNameActions.edit());

    save() {
        if (this.nameForm.valid) {
            this.store.dispatch(AssetNameActions.save(this.nameForm.getRawValue()));
        }
    }

    cancel = () => this.store.dispatch(AssetNameActions.cancel());

    selectMergeTarget(searchResultReference: string) {
        const regex = /^\/assets\/(\d+)$/;
        const match = searchResultReference.match(regex);
        if (match) {
            const mergeTargetId = Number(match[1]);
            this.store.dispatch(AssetActions.setMergeTarget({mergeTargetId}));
        }
    }

    deselectAssetMergeTarget() {
        this.store.dispatch(AssetActions.setMergeTarget({mergeTargetId: undefined}));
    }

    displaySearch() {
        this.searchEnabled = !this.searchEnabled;
    }

    onAssetMenuButtonClick(event: Event): void {
        // Remove focus from the button to avoid the aria-hidden conflict. Without this, the focused button is hidden and triggers an accessibility error.
        (event.currentTarget as HTMLElement).blur();
    }

    openArchiveConfirmation(archived: boolean): void {
        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = `Are you sure you want to ${archived ? "archive" : "unarchive"} this asset?`;

        dialogRef.afterClosed().pipe(
            map(result => {
                if (result && this.assetId != null) {
                    this.store.dispatch(AssetActions.archive({archived}));
                }
            })
        ).subscribe();
    }

    openDocumentGeneratorDialog() {
        this.store.select(selectAssetTemplateContext)
            .pipe(first(context => !!context))
            .subscribe(context => {
                const dialogRef = this.dialog.open(DocumentGeneratorComponent);
                dialogRef.componentInstance.context = context;
            });
    }
}
